import React, { useContext } from 'react';
import { PrescreenTypes } from 'global';
import { AftermarketLicenceType } from 'modules/desking/types';
import {
  AutocomleteFieldSetting,
  DocumentsByTypeSettings,
  MultipleSelectFieldSetting,
  PrescreenManagementTypeHelperText,
  PrescreenManagementTypeLabel,
  RolesSetting,
  SelectFieldSetting,
  Switcher,
  TextFieldSetting,
} from 'modules/portalConfigurations/ui/configurableItemsComponents';
import { configurableItemsTypes } from 'pages/Prequal/constants';
import { rolesSettingsKeys } from 'services/pcwService';
import { vendorContactRoleHumanReadable } from 'utils';
import { CommonDataContext } from 'contexts/CommonDataContext';

export function useConfigurableItemsSettings() {
  const { isAftermarketPartner } = useContext(CommonDataContext);

  const configurableItemsSettings = {
    [configurableItemsTypes.section]: {
      title: 'Section settings',
      settings: [
        {
          label: 'Title',
          configurationKey: 'title',
          component: TextFieldSetting,
        },
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.block]: {
      title: 'Block settings',
      settings: [
        {
          label: 'Title',
          configurationKey: 'title',
          component: TextFieldSetting,
        },
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.field]: {
      title: 'Field settings',
      settings: [
        {
          label: 'Title',
          configurationKey: 'title',
          component: TextFieldSetting,
        },
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.scorecard]: {
      title: 'Scorecard Settings',
      settings: [
        {
          label: 'Select the Lender whose card to show on the VO page:',
          configurationKey: 'lenderProfileId',
          component: AutocomleteFieldSetting,
        },
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.leads]: {
      title: 'Leads',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.opportunities]: {
      title: 'Opportunities & Proposals',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
          customHelperText: {
            [rolesSettingsKeys.hidden]: 'Opportunities and Proposals will be hidden for selected roles',
            [rolesSettingsKeys.assignedOnly]:
              'The selected roles will be able to see only those Opportunities and Proposals that are assigned to them',
            [rolesSettingsKeys.locationBased]:
              'Selected roles will be able to see only Opportunities and Proposals from their location',
          },
        },
        {
          label: 'Manual Vendor Opportunity Creation:',
          configurationKey: 'allowManualVoCreation',
          component: Switcher,
        },
        {
          label: 'Allow Submissions V2:',
          configurationKey: 'allowSubmissionsV2',
          component: Switcher,
        },
        {
          label: 'Allow Deal Pipeline:',
          configurationKey: 'allowDealPipeline',
          component: Switcher,
        },
        {
          label: 'Inventory Search hidden for:',
          configurationKey: 'hiddenInventorySearch',
          component: MultipleSelectFieldSetting,
          selectLabel: 'Hidden for:',
          helperText: 'Inventory Search will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).map(([value, label]) => ({ label, value })),
        },
      ],
    },
    [configurableItemsTypes.invoiceAndEquipment]: {
      title: 'Invoice and Equipment',
      settings: [
        {
          label: 'Allow Invoice and Equipment V2:',
          configurationKey: 'allowInvoiceAndEquipmentV2',
          component: Switcher,
        },
      ],
    },
    [configurableItemsTypes.submissionsHubManagement]: {
      title: 'Submissions Hub',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
          customHelperText: {
            [rolesSettingsKeys.hidden]: 'Submissions Hub will be hidden for selected roles',
            [rolesSettingsKeys.assignedOnly]:
              'The selected roles will be able to see only those Submissions on Opportunities that they are assigned to.',
            [rolesSettingsKeys.locationBased]:
              'Selected roles will be able to see only Submissions on Opportunities whose location they are configured for under Location Management',
          },
        },
      ],
    },
    [configurableItemsTypes.userManagement]: {
      title: 'User Management',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.securityManagement]: {
      title: 'Security Management',
      settings: [
        {
          label: 'MFA Enabled By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.healthCheck]: {
      title: 'Health Check',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.creditReports]: {
      title: 'Access to Pull Credit Reports',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.documents]: {
      title: 'Documents Settings',
      settings: [
        {
          label: 'Allow Credit Application for the following Lenders:',
          configurationKey: 'creditAppHiddenForLenderProfileIds',
          component: AutocomleteFieldSetting,
        },
        /*       {
          label: "Allow Credit Reports for the following Lenders:",
          configurationKey: "creditReportsHiddenForLenderProfileIds",
          component: AutocomleteFieldSetting,
        }, */
        {
          label: 'Documents By Type Settings:',
          configurationKey: 'documentsByTypeSettings',
          component: DocumentsByTypeSettings,
        },
      ],
    },
    [configurableItemsTypes.portalSetup]: {
      title: 'Portal Setup Settings',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.voManagement]: {
      title: 'Opportunity Management',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          optionsByRoleSettingKey: {
            [rolesSettingsKeys.cadmReadOnly]: {
              label: 'Ability to Edit Credit Applications',
              helperText: 'Only user roles selected here will have this ability',
              isInversed: true,
            },
            [rolesSettingsKeys.disallowVoСreation]: {
              label: 'Create New Opportunity Manually',
              helperText: 'Only user roles selected here will have this ability',
              isInversed: true,
            },
          },
          component: RolesSetting,
        },
        {
          label: 'Desking tab:',
          variant: 'standard',
          component: MultipleSelectFieldSetting,
          configurationKey: 'deskingTabHiddenFor',
          selectLabel: 'Hidden for:',
          helperText: 'Desking tab will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).reduce(
            (acc: any, [value, label]) => [...acc, { label, value }],
            []
          ),
        },
        {
          label: 'Submissions tab:',
          variant: 'standard',
          component: MultipleSelectFieldSetting,
          configurationKey: 'submissionsTabHiddenFor',
          selectLabel: 'Hidden for:',
          helperText: 'Submissions tab will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).reduce(
            (acc: any, [value, label]) => [...acc, { label, value }],
            []
          ),
        },
      ],
    },
    [configurableItemsTypes.aftermarket]: {
      title: 'Aftermarket Management',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          optionsByRoleSettingKey: {
            [rolesSettingsKeys.assignedOnly]: {
              label: 'Able to change/update Aftermarket Products Page',
              helperText: 'Select user roles that will have this ability',
              isInversed: false,
            },
            [rolesSettingsKeys.readOnly]: {
              label: 'Able to read/see Aftermarket Products Page',
              helperText: 'Select user roles that will have this ability',
              isInversed: false,
            },
          },
          component: RolesSetting,
        },
        {
          label: 'Licence:',
          variant: 'standard',
          defaultValue: AftermarketLicenceType.Core,
          selectLabel: 'Licence Type',
          component: SelectFieldSetting,
          configurationKey: 'aftermarketLicenceType',
          options: [
            { label: 'Core', value: AftermarketLicenceType.Core },
            { label: 'Enterprise', value: AftermarketLicenceType.Enterprise },
          ],
        },
        {
          component: Switcher,
          label: 'Allow creation of Manual Product cards in Desking:',
          configurationKey: 'allowCreateCustomProducts',
        },
        {
          component: Switcher,
          label: 'Direct to Customer: Enable Send Proposals',
          configurationKey: 'partnerAllowSendProposals',
          isHidden: !isAftermarketPartner,
        },
      ],
    },
    [configurableItemsTypes.prescreen]: {
      title: 'Prescreen Management',
      settings: [
        {
          label: React.createElement(PrescreenManagementTypeLabel),
          configurationKey: rolesSettingsKeys.prescreenType,
          component: SelectFieldSetting,
          selectLabel: 'Type',
          helperText: React.createElement(PrescreenManagementTypeHelperText),
          options: [
            { label: 'Prescreen', value: PrescreenTypes.Prescreen },
            { label: 'LenderWaterfall', value: PrescreenTypes.LenderWaterfall },
            { label: 'Off', value: PrescreenTypes.Off },
            { label: 'Demo', value: PrescreenTypes.Demo },
          ],
        },
        {
          label: 'Preescreen Tools hidden for',
          configurationKey: rolesSettingsKeys.hiddenPrescreenPage,
          component: MultipleSelectFieldSetting,
          selectLabel: 'Hidden for:',
          helperText: 'Preescreen Tools will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).reduce((acc: any, [value, label]) => {
            acc.push({ label, value });
            return acc;
          }, []),
        },
        {
          label: 'Prescreen results hidden for',
          configurationKey: rolesSettingsKeys.hiddenPrescreenResults,
          component: MultipleSelectFieldSetting,
          selectLabel: 'Hidden for:',
          helperText: 'Prescreen results will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).reduce((acc: any, [value, label]) => {
            acc.push({ label, value });
            return acc;
          }, []),
        },
        {
          label: 'Submission Access hidden for',
          configurationKey: rolesSettingsKeys.hiddenPrescreenSubmissionAccess,
          component: MultipleSelectFieldSetting,
          selectLabel: 'Hidden for:',
          helperText: 'Submission Access will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).reduce((acc: any, [value, label]) => {
            acc.push({ label, value });
            return acc;
          }, []),
        },
        {
          label: 'Scorecard Builder hidden for',
          configurationKey: rolesSettingsKeys.hiddenPrescreenScorecardBuilder,
          component: MultipleSelectFieldSetting,
          selectLabel: 'Hidden for:',
          helperText: 'Scorecard Builder will be hidden for',
          options: Object.entries(vendorContactRoleHumanReadable).reduce((acc: any, [value, label]) => {
            acc.push({ label, value });
            return acc;
          }, []),
        },
      ],
    },
    [configurableItemsTypes.insurance]: {
      title: 'Insurance',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.customers]: {
      title: 'Customers',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          component: RolesSetting,
        },
      ],
    },
    [configurableItemsTypes.inventory]: {
      title: 'Inventory Management',
      settings: [
        {
          label: 'Settings By Roles:',
          configurationKey: 'rolesSettings',
          optionsByRoleSettingKey: {
            [rolesSettingsKeys.assignedOnly]: {
              label: 'Able to change/update Inventory Page',
              helperText: 'Select user roles that will have this ability',
              isInversed: false,
            },
            [rolesSettingsKeys.readOnly]: {
              label: 'Able to read/see Inventory Page',
              helperText: 'Select user roles that will have this ability',
              isInversed: false,
            },
          },
          component: RolesSetting,
        },
      ],
    },
  };

  return {
    configurableItemsSettings,
  };
}
